import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TIERS = ["senior", "junior", "new_joiner"];
const TIER_LABEL = {
  senior: "Senior R.A.",
  junior: "Junior R.A.",
  new_joiner: "New Joiner / Fresher",
};
const METRICS = [
  ["leads_per_day", "Leads / day"],
  ["positive_clients_per_week", "Positive clients / week"],
  ["closers_per_quarter", "Closers / quarter"],
  ["placements_per_quarter", "Placements / quarter"],
];

export default function RAConfig() {
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(30);
  const [taxonomy, setTaxonomy] = useState({ industries: [], job_titles: [] });
  const [tiers, setTiers] = useState(null);
  const [ras, setRas] = useState([]);
  const [raTiers, setRaTiers] = useState({});

  useEffect(() => {
    api.get("/ra/config").then(({ data }) => { setCfg(data); setDays(data.dup_window_days); }).catch((e) => toast.error(apiErr(e)));
    api.get("/ra/taxonomy").then(({ data }) => setTaxonomy(data)).catch((e) => toast.error(apiErr(e)));
    api.get("/ra/target-defaults").then(({ data }) => setTiers(data.tiers)).catch((e) => toast.error(apiErr(e)));
    api.get("/ra/team-performance")
      .then(({ data }) => {
        setRas(data.rows.map((r) => ({ id: r.ra_user_id, name: r.ra_name })));
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/ra/config", { dup_window_days: Number(days) });
      setCfg(data);
      await api.put("/ra/taxonomy", { industries: taxonomy.industries, job_titles: taxonomy.job_titles });
      toast.success("Configuration saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveDefaults = async () => {
    try {
      const { data } = await api.put("/ra/target-defaults", { tiers });
      setTiers(data.tiers);
      toast.success("Default targets saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setTierVal = (tier, metric, val) =>
    setTiers((prev) => ({ ...prev, [tier]: { ...prev[tier], [metric]: Number(val) } }));

  const assignTier = async (raId, tier) => {
    try {
      await api.post("/ra/targets", { ra_user_id: raId, tier });
      setRaTiers((prev) => ({ ...prev, [raId]: tier }));
      toast.success("Tier assigned");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!cfg) return <Skeleton className="h-48" />;

  return (
    <div className="space-y-4">
      <PageHeader title="R.A. Configuration" subtitle="Duplicate-protection, sourcing policy, and tier targets" />

      <Card className="p-6 max-w-3xl space-y-5" data-testid="ra-configuration-card">
        <div>
          <label className="text-sm font-medium">Duplicate window (days)</label>
          <p className="text-xs text-muted-foreground mb-2">A company + position + location cannot be re-researched within this window without an Admin override.</p>
          <Input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} data-testid="ra-config-days" className="max-w-[140px]" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">Approved industries</label>
            <p className="text-xs text-muted-foreground mb-2">One industry per line.</p>
            <Textarea value={taxonomy.industries.join("\n")} onChange={(e) => setTaxonomy({ ...taxonomy, industries: e.target.value.split("\n") })} data-testid="ra-taxonomy-industries" className="min-h-40" />
          </div>
          <div>
            <label className="text-sm font-medium">Approved research titles</label>
            <p className="text-xs text-muted-foreground mb-2">One title per line.</p>
            <Textarea value={taxonomy.job_titles.join("\n")} onChange={(e) => setTaxonomy({ ...taxonomy, job_titles: e.target.value.split("\n") })} data-testid="ra-taxonomy-titles" className="min-h-40" />
          </div>
        </div>
        <Button onClick={save} data-testid="ra-config-save" className="bg-[#255058] hover:bg-[#1c3d43] text-white">Save Configuration</Button>
      </Card>

      <Card className="p-6 space-y-4" data-testid="ra-tier-defaults-card">
        <div>
          <div className="font-display text-lg font-semibold">Default R.A. targets by tier</div>
          <p className="text-xs text-muted-foreground">Edit the default goals for each tier. Changes apply to future calculations; historical targets are preserved.</p>
        </div>
        {!tiers ? <Skeleton className="h-32" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-4">Tier</th>
                  {METRICS.map(([, label]) => <th key={label} className="py-2 pr-4">{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {TIERS.map((tier) => (
                  <tr key={tier} className="border-b border-border/60" data-testid={`ra-tier-row-${tier}`}>
                    <td className="py-2 pr-4 font-medium">{TIER_LABEL[tier]}</td>
                    {METRICS.map(([metric]) => (
                      <td key={metric} className="py-2 pr-4">
                        <Input type="number" min={0} value={tiers[tier]?.[metric] ?? 0}
                          onChange={(e) => setTierVal(tier, metric, e.target.value)}
                          data-testid={`ra-tier-${tier}-${metric}`} className="w-24" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Button onClick={saveDefaults} data-testid="ra-tier-defaults-save" className="bg-[#255058] hover:bg-[#1c3d43] text-white">Save Default Targets</Button>
      </Card>

      <Card className="p-6 space-y-4" data-testid="ra-tier-assign-card">
        <div>
          <div className="font-display text-lg font-semibold">Assign R.A. tiers</div>
          <p className="text-xs text-muted-foreground">Set each analyst's tier. Their targets follow the tier defaults above.</p>
        </div>
        {ras.length === 0 ? (
          <div className="text-sm text-muted-foreground">No R.A.s in your visible scope.</div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {ras.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2" data-testid={`ra-tier-assign-${r.id}`}>
                <span className="text-sm font-medium">{r.name}</span>
                <Select value={raTiers[r.id] || ""} onValueChange={(v) => assignTier(r.id, v)}>
                  <SelectTrigger className="w-44" data-testid={`ra-tier-select-${r.id}`}><SelectValue placeholder="Set tier" /></SelectTrigger>
                  <SelectContent>
                    {TIERS.map((tier) => <SelectItem key={tier} value={tier}>{TIER_LABEL[tier]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
